import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Icon, CodeToken, CopyButton, IconName, Modal, ServerLogo } from '../components/ui';
import { copyToClipboard as clipboardWrite } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface OnboardingRequestStatusProps {
  apiUrl: string;
}

interface McpTool {
  name: string;
  description: string;
  input_schema?: {
    type?: string;
    properties?: Record<string, any>;
    required?: string[];
  };
}

interface McpService {
  server_id: string;
  name: string;
  tools?: McpTool[];
}

interface OnboardingRequest {
  request_id: string;
  agent_id: string;
  agent_name: string;
  requested_role: string;
  services: McpService[];
  service_ids?: string[];
  request_description: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  created_at: string;
  created_by: string;
  approved_at?: string;
  approved_by?: string;
  rejection_reason?: string;
  apiKey?: string;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-redact',
  approved: 'badge-success',
  completed: 'badge-success',
  rejected: 'badge-error'
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending Approval',
  approved: 'Approved',
  completed: 'Ready to Download',
  rejected: 'Rejected'
};

const STATUS_ICON: Record<string, IconName> = {
  pending: 'bell',
  approved: 'checkCircle',
  completed: 'checkCircle',
  rejected: 'deny'
};

const STRING_EXAMPLES: Record<string, string> = {
  realm: 'master',
  username: 'jdoe',
  email: 'jdoe@example.com',
  firstName: 'Jane',
  lastName: 'Doe',
  userId: 'user-id',
  roleName: 'developer'
};

const indentBlock = (text: string, spaces: number): string => {
  const indent = ' '.repeat(spaces);
  return text
    .split('\n')
    .map((line) => `${indent}${line}`)
    .join('\n');
};

const getExampleValue = (fieldName: string, schema: Record<string, any> = {}): any => {
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return schema.enum[0];
  }

  switch (schema.type) {
    case 'boolean':
      return false;
    case 'integer':
    case 'number':
      return 1;
    case 'array':
      return [];
    case 'object':
      return {};
    case 'string':
    default:
      return STRING_EXAMPLES[fieldName] || `your-${fieldName}`;
  }
};

const buildExampleArguments = (tool?: McpTool): Record<string, any> => {
  const schema = tool?.input_schema;
  const properties = schema?.properties || {};
  const requiredFields = Array.isArray(schema?.required) ? schema.required : [];

  return requiredFields.reduce<Record<string, any>>((args, fieldName) => {
    args[fieldName] = getExampleValue(fieldName, properties[fieldName]);
    return args;
  }, {});
};

const OnboardingRequestStatus: React.FC<OnboardingRequestStatusProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { requestId } = useParams<{ requestId: string }>();
  const [request, setRequest] = useState<OnboardingRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [notificationModal, setNotificationModal] = useState<{ open: boolean; title: string; message: string }>({ open: false, title: '', message: '' });
  const [cursorConfig, setCursorConfig] = useState<string | null>(null);
  const [claudeConfig, setClaudeConfig] = useState<string | null>(null);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [activeTab, setActiveTab] = useState<'cursor' | 'claude' | 'javascript' | 'python' | 'curl'>('cursor');
  const [copiedTab, setCopiedTab] = useState<string | null>(null);
  const [jsCode, setJsCode] = useState<string>('');
  const [pythonCode, setPythonCode] = useState<string>('');
  const [curlCode, setCurlCode] = useState<string>('');

  useEffect(() => {
    if (requestId) {
      fetchRequest(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, apiUrl]);

  // Fetch config files when request is completed
  useEffect(() => {
    if (request?.status === 'completed' && requestId) {
      fetchConfigs();
      generateCodeExamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.status, requestId]);

  const generateCodeExamples = () => {
    if (!request?.services || request.services.length === 0) return;

    // Public base URL for the generated snippets. The MCP endpoint is the
    // firewall (apiUrl, e.g. http://ztai.fidar.ai:3000) — NOT the dashboard
    // origin (which is the dashboard's port, 3100). apiUrl itself is derived
    // at runtime from the browser host in App.tsx, so this tracks the
    // deployment without a rebuild.
    const publicBase = (process.env.REACT_APP_MCP_BASE_URL || apiUrl || window.location.origin).replace(/\/$/, '');

    const service = request.services[0];
    const serviceName = service.name.replace(/\s+/g, '-').toLowerCase();
    const tool = service.tools?.[0];
    const toolName = tool?.name || 'example_tool';
    const apiKey = request.apiKey || 'YOUR_API_KEY_HERE';
    const exampleArguments = serviceName === 'fidar' ? buildExampleArguments(tool) : {};
    const payload = {
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: exampleArguments
      }
    };
    const payloadJson = JSON.stringify(payload, null, 2);

    const jsExample = `const response = await fetch(
  '${publicBase}/mcp-http/${serviceName}',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ${apiKey}',
    },
    body: JSON.stringify(
${indentBlock(payloadJson, 6)}
    )
  }
);

const result = await response.json();
console.log(result);`;

    const pythonExample = `import requests
import json

response = requests.post(
    '${publicBase}/mcp-http/${serviceName}',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ${apiKey}'
    },
  json=json.loads('''
${payloadJson}
''')
)

result = response.json()
print(json.dumps(result, indent=2))`;

    const curlExample = `curl -X POST ${publicBase}/mcp-http/${serviceName} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${payloadJson}'`;

    setJsCode(jsExample);
    setPythonCode(pythonExample);
    setCurlCode(curlExample);
  };

  // Auto-refresh every 5 seconds if status is pending
  useEffect(() => {
    if (!request || request.status !== 'pending') return;

    const interval = setInterval(() => {
      fetchRequest(true);
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.status, requestId, apiUrl]);

  const fetchRequest = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(`${apiUrl}/admin/onboarding/requests/${requestId}`);
      if (response.ok) {
        const data = await response.json();
        setRequest(data);
        if (isRefresh) {
          setLastRefresh(new Date().toLocaleTimeString());
        }
      } else if (response.status === 404) {
        setError('Onboarding request not found. Check your request ID.');
      } else {
        setError('Failed to load request details');
      }
    } catch (error) {
      console.error('Failed to fetch request:', error);
      setError('Error loading request details');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchRequest(true);
  };

  const fetchConfigs = async () => {
    setLoadingConfigs(true);
    try {
      // Fetch Cursor config
      const cursorRes = await fetch(`${apiUrl}/onboarding/requests/${requestId}/download/cursor-config`);
      if (cursorRes.ok) {
        const text = await cursorRes.text();
        setCursorConfig(text);
      }

      // Fetch Claude config
      const claudeRes = await fetch(`${apiUrl}/onboarding/requests/${requestId}/download/claude-config`);
      if (claudeRes.ok) {
        const text = await claudeRes.text();
        setClaudeConfig(text);
      }
    } catch (err) {
      console.error('Failed to fetch configs:', err);
    } finally {
      setLoadingConfigs(false);
    }
  };

  const copyToClipboard = (text: string, tabName: string) => {
    clipboardWrite(text).then((ok) => {
      if (!ok) return;
      setCopiedTab(tabName);
      setTimeout(() => setCopiedTab(null), 2000);
    });
  };

  const handleDownload = async (fileType: string) => {
    try {
      setDownloadingFile(fileType);

      const response = await fetch(`${apiUrl}/onboarding/requests/${requestId}/download/${fileType}`);

      if (response.ok) {
        const blob = await response.blob();

        // Determine filename based on type
        let filename = '';
        let contentType = 'text/plain';

        switch (fileType) {
          case 'proxy-script':
            filename = 'ztai-firewall-mcp.js';
            contentType = 'application/javascript';
            break;
          case 'claude-config':
            filename = 'claude-config.json';
            contentType = 'application/json';
            break;
          case 'cursor-config':
            filename = 'cursor-config.json';
            contentType = 'application/json';
            break;
          case 'setup-guide':
            filename = 'SETUP_GUIDE.md';
            contentType = 'text/markdown';
            break;
        }

        // Create download link
        const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setNotificationModal({
          open: true,
          title: t('pages.onboardingRequestStatus.downloadFailed'),
          message: `Failed to download ${fileType}`
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      setNotificationModal({
        open: true,
        title: t('pages.onboardingRequestStatus.error'),
        message: t('pages.onboardingRequestStatus.errorDownloadingFile')
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <h1>
          <Icon name="search" /> {t('pages.onboardingRequestStatus.onboardingRequestStatus')}
        </h1>
        <p className="muted-text">{t('pages.onboardingRequestStatus.fetchingYourOnboardingRequest')}</p>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="page-content">
        <h1>
          <Icon name="search" /> {t('pages.onboardingRequestStatus.onboardingRequestStatus')}
        </h1>
        <div className="notice error" style={{ marginTop: 16 }}>
          <Icon name="warning" />
          <span>
            <strong>{t('pages.onboardingRequestStatus.error')}</strong> {error || t('pages.onboardingRequestStatus.requestNotFound')}
          </span>
        </div>
        <p className="muted-text" style={{ marginTop: 16 }}>
          {t('pages.onboardingRequestStatus.makeSureYouHaveTheCorrect')}
        </p>
      </div>
    );
  }

  const isCompleted = request.status === 'completed';
  const isRejected = request.status === 'rejected';
  const isPending = request.status === 'pending';

  return (
    <div className="page-content">
      <h1>
        <Icon name="search" size={20} /> {t('pages.onboardingRequestStatus.onboardingRequestStatus')}
      </h1>

      {/* Request Details Card */}
      <div className="ent-card" style={{ marginTop: 18, marginBottom: 24 }}>
        <div className="ent-card-body">
          {/* Status Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 24,
              paddingBottom: 24,
              borderBottom: '1px solid var(--border)',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
              <div
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--radius)',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-2)'
                }}
              >
                <Icon name={STATUS_ICON[request.status] || 'info'} size={24} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  {t('pages.onboardingRequestStatus.request')}
                  <span className={`badge ${STATUS_BADGE[request.status] || 'badge-unknown'}`}>
                    {STATUS_LABEL[request.status] || request.status}
                  </span>
                </h2>
                <span className="copy-cell">
                  <CodeToken muted>{request.request_id}</CodeToken>
                  <CopyButton value={request.request_id} title={t('pages.onboardingRequestStatus.copyRequestID')} />
                </span>
              </div>
            </div>
            <button
              className="ent-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              title={lastRefresh ? `Last refreshed: ${lastRefresh}` : t('pages.onboardingRequestStatus.refreshStatus')}
            >
              <Icon name="refresh" className={refreshing ? 'spin' : ''} />
              {refreshing ? t('pages.onboardingRequestStatus.refreshing') : t('pages.onboardingRequestStatus.refresh')}
            </button>
          </div>

          {/* Request Info Grid */}
          <div className="detail-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 0 }}>
            <div>
              <span className="detail-label">{t('pages.onboardingRequestStatus.agentName')}</span>
              <span style={{ fontSize: 14, color: 'var(--text)' }}>{request.agent_name}</span>
            </div>
            <div>
              <span className="detail-label">{t('pages.onboardingRequestStatus.agentID')}</span>
              <span className="copy-cell">
                <CodeToken>{request.agent_id}</CodeToken>
                <CopyButton value={request.agent_id} title={t('pages.onboardingRequestStatus.copyAgentID')} />
              </span>
            </div>
            <div>
              <span className="detail-label">{t('pages.onboardingRequestStatus.role')}</span>
              <span style={{ fontSize: 14, color: 'var(--text)' }}>
                {request.requested_role.charAt(0).toUpperCase() + request.requested_role.slice(1)}
              </span>
            </div>
            <div>
              <span className="detail-label">{t('pages.onboardingRequestStatus.created')}</span>
              <span style={{ fontSize: 14, color: 'var(--text)' }}>{parseServerDate(request.created_at).toLocaleDateString()}</span>
            </div>
            {request.approved_at && (
              <div>
                <span className="detail-label">{t('pages.onboardingRequestStatus.approved')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{parseServerDate(request.approved_at).toLocaleDateString()}</span>
              </div>
            )}
            <div>
              <span className="detail-label">{t('pages.onboardingRequestStatus.services')}</span>
              <span style={{ fontSize: 14, color: 'var(--text)' }}>{t('pages.onboardingRequestStatus.serviceS', { v0: request.services.length })}</span>
            </div>
          </div>

          {/* Services List */}
          {request.services && request.services.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <span className="detail-label">{isPending ? t('pages.onboardingRequestStatus.requestedServices') : t('pages.onboardingRequestStatus.grantedAccess')}</span>
              <div className="token-row" style={{ marginTop: 8 }}>
                {request.services.map((service) => (
                  <div
                    key={service.server_id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      fontSize: 13,
                      color: 'var(--text-2)'
                    }}
                  >
                    <ServerLogo hostname={service.name} apiUrl={apiUrl} proxyService={service.name} size={16} />
                    <span>{t('pages.onboardingRequestStatus.tools', { v0: service.name, v1: service.tools?.length || 0 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {request.request_description && (
            <div style={{ marginTop: 20 }}>
              <span className="detail-label">{t('pages.onboardingRequestStatus.description')}</span>
              <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                {request.request_description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Status Messages and Downloads */}
      {isPending && (
        <div className="notice warn" style={{ alignItems: 'flex-start' }}>
          <Icon name="bell" />
          <div>
            <strong>{t('pages.onboardingRequestStatus.awaitingApproval')}</strong>
            <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
              {t('pages.onboardingRequestStatus.yourOnboardingRequestIsPendingAdmin')}
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-3)' }}>
              {t('pages.onboardingRequestStatus.thisPageRefreshesAutomaticallyOrUse')}
            </div>
          </div>
        </div>
      )}

      {isRejected && (
        <div className="notice error" style={{ alignItems: 'flex-start' }}>
          <Icon name="deny" />
          <div>
            <strong>{t('pages.onboardingRequestStatus.requestRejected')}</strong>
            <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
              {t('pages.onboardingRequestStatus.yourOnboardingRequestWasRejectedPlease')}
            </div>
            {request.rejection_reason && (
              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-3)' }}>
                <strong>{t('pages.onboardingRequestStatus.reason')}</strong> {request.rejection_reason}
              </div>
            )}
          </div>
        </div>
      )}

      {isCompleted && request.apiKey && (
        <div className="ent-card" style={{ marginBottom: 24, borderColor: 'var(--accent)', borderWidth: 2 }}>
          <div className="ent-card-head">
            <h3>
              <Icon name="key" size={20} style={{ marginRight: 6 }} />
              {t('pages.onboardingRequestStatus.yourAPIKey')}
            </h3>
          </div>
          <div className="ent-card-body">
            <p className="muted-text" style={{ marginBottom: 12 }}>
              {t('pages.onboardingRequestStatus.useThisAPIKeyToAuthenticate')}
            </p>
            <div
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: 12,
                fontFamily: 'var(--mono)',
                fontSize: 13,
                color: 'var(--text)',
                wordBreak: 'break-all',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
              }}
            >
              <span>{request.apiKey}</span>
              <CopyButton value={request.apiKey} title={t('pages.onboardingRequestStatus.copyAPIKey')} />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
              {t('pages.onboardingRequestStatus.includeThisInThe')} <strong>{t('pages.onboardingRequestStatus.authorization')}</strong> {t('pages.onboardingRequestStatus.headerAs')} <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 3 }}>Bearer {request.apiKey}</code>
            </p>
          </div>
        </div>
      )}

      {isCompleted && (
        <div className="ent-card">
          <div className="ent-card-head">
            <h3>
              <Icon name="settings" size={20} style={{ marginRight: 6 }} />
              {t('pages.onboardingRequestStatus.integrationGuide')}
            </h3>
          </div>
          <div className="ent-card-body" style={{ padding: 0 }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', paddingLeft: 16 }}>
              {(['cursor', 'claude', 'javascript', 'python', 'curl'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 16px',
                    border: 'none',
                    background: activeTab === tab ? 'var(--accent)' : 'transparent',
                    color: activeTab === tab ? '#fff' : 'var(--text-2)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: activeTab === tab ? 600 : 500,
                    borderBottom: activeTab === tab ? 'none' : '1px solid transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ padding: '20px' }}>
              {loadingConfigs && <p className="muted-text">{t('pages.onboardingRequestStatus.loadingConfigurations')}</p>}

              {/* Cursor Tab */}
              {activeTab === 'cursor' && !loadingConfigs && cursorConfig && (
                <div>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {t('pages.onboardingRequestStatus.cursorConfiguration')}
                  </h4>

                  {/* Config Display */}
                  <div
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      padding: 12,
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      overflow: 'auto',
                      maxHeight: 250,
                      color: 'var(--text-2)',
                      lineHeight: 1.4,
                      marginBottom: 12
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {cursorConfig}
                    </pre>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <button
                      className={`ent-btn ${copiedTab === 'cursor-copy' ? 'success' : ''}`}
                      onClick={() => copyToClipboard(cursorConfig, 'cursor-copy')}
                      style={{ fontSize: 12 }}
                    >
                      <Icon name={copiedTab === 'cursor-copy' ? 'check' : 'copy'} size={14} />
                      {copiedTab === 'cursor-copy' ? t('pages.onboardingRequestStatus.copied') : t('pages.onboardingRequestStatus.copyConfig')}
                    </button>
                    <button
                      className="ent-btn ghost"
                      onClick={() => handleDownload('cursor-config')}
                      disabled={downloadingFile === 'cursor-config'}
                      style={{ fontSize: 12 }}
                    >
                      <Icon name={downloadingFile === 'cursor-config' ? 'refresh' : 'download'} size={14} />
                      {downloadingFile === 'cursor-config' ? t('pages.onboardingRequestStatus.downloading') : t('pages.onboardingRequestStatus.download')}
                    </button>
                  </div>

                  {/* Setup Instructions */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {t('pages.onboardingRequestStatus.setupSteps')}
                    </h5>
                    <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.8 }}>
                      <li>
                        {t('pages.onboardingRequestStatus.openCursorSettingsCmdCtrl')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.navigateTo')} <strong>{t('pages.onboardingRequestStatus.featuresMCPServers')}</strong>
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.click')} <strong>{t('pages.onboardingRequestStatus.addServer')}</strong>
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.pasteTheConfigurationAbove')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.restartCursor')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.yourMCPToolsWillNowAppear')}
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {/* Claude Tab */}
              {activeTab === 'claude' && !loadingConfigs && claudeConfig && (
                <div>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {t('pages.onboardingRequestStatus.claudeDesktopConfiguration')}
                  </h4>

                  {/* Proxy Script Download */}
                  <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 'var(--radius)', marginBottom: 16 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-2)' }}>
                      {t('pages.onboardingRequestStatus.firstDownloadTheProxyScript')}
                    </p>
                    <button
                      className="ent-btn primary"
                      onClick={() => handleDownload('proxy-script')}
                      disabled={downloadingFile === 'proxy-script'}
                      style={{ fontSize: 12 }}
                    >
                      <Icon name={downloadingFile === 'proxy-script' ? 'refresh' : 'download'} size={14} />
                      {downloadingFile === 'proxy-script' ? t('pages.onboardingRequestStatus.downloading') : t('pages.onboardingRequestStatus.downloadProxyScript')}
                    </button>
                  </div>

                  {/* Config Display */}
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>
                      {t('pages.onboardingRequestStatus.thenUpdateYourConfigFile')}
                    </p>
                    <div
                      style={{
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        padding: 12,
                        fontFamily: 'var(--mono)',
                        fontSize: 11,
                        overflow: 'auto',
                        maxHeight: 250,
                        color: 'var(--text-2)',
                        lineHeight: 1.4
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {claudeConfig}
                      </pre>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <button
                      className={`ent-btn ${copiedTab === 'claude-copy' ? 'success' : ''}`}
                      onClick={() => copyToClipboard(claudeConfig, 'claude-copy')}
                      style={{ fontSize: 12 }}
                    >
                      <Icon name={copiedTab === 'claude-copy' ? 'check' : 'copy'} size={14} />
                      {copiedTab === 'claude-copy' ? t('pages.onboardingRequestStatus.copied') : t('pages.onboardingRequestStatus.copyConfig')}
                    </button>
                    <button
                      className="ent-btn ghost"
                      onClick={() => handleDownload('claude-config')}
                      disabled={downloadingFile === 'claude-config'}
                      style={{ fontSize: 12 }}
                    >
                      <Icon name={downloadingFile === 'claude-config' ? 'refresh' : 'download'} size={14} />
                      {downloadingFile === 'claude-config' ? t('pages.onboardingRequestStatus.downloading') : t('pages.onboardingRequestStatus.download')}
                    </button>
                  </div>

                  {/* Setup Instructions */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {t('pages.onboardingRequestStatus.setupSteps')}
                    </h5>
                    <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.8 }}>
                      <li>
                        {t('pages.onboardingRequestStatus.downloadThe')} <strong>{t('pages.onboardingRequestStatus.proxyScript')}</strong> {t('pages.onboardingRequestStatus.above')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.openYourClaudeConfigFile')} <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>~/.claude/claude.json</code>
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.replace')} <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>/path/to/proxy-script.js</code> {t('pages.onboardingRequestStatus.withTheActualPathToThe')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.pasteTheConfigurationAboveIntoThe')} <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>mcpServers</code> {t('pages.onboardingRequestStatus.section')}
                      </li>
                      <li>
                        {t('pages.onboardingRequestStatus.restartClaudeDesktop')}
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {/* JavaScript Tab */}
              {activeTab === 'javascript' && jsCode && (
                <div>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {t('pages.onboardingRequestStatus.javascriptNodeJsCallMCPServers')}
                  </h4>
                  <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-3)' }}>
                    {t('pages.onboardingRequestStatus.editCodeHint')} {request?.services?.map(s => s.name).join(', ')}
                  </p>
                  <textarea
                    value={jsCode}
                    onChange={(e) => setJsCode(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: 280,
                      padding: 12,
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      color: 'var(--text-2)',
                      lineHeight: 1.4,
                      marginBottom: 12,
                      resize: 'vertical'
                    }}
                  />
                  <button
                    className={`ent-btn ${copiedTab === 'js-copy' ? 'success' : ''}`}
                    onClick={() => copyToClipboard(jsCode, 'js-copy')}
                    style={{ fontSize: 12 }}
                  >
                    <Icon name={copiedTab === 'js-copy' ? 'check' : 'copy'} size={14} />
                    {copiedTab === 'js-copy' ? t('pages.onboardingRequestStatus.copied') : t('pages.onboardingRequestStatus.copyCode')}
                  </button>
                </div>
              )}

              {/* Python Tab */}
              {activeTab === 'python' && pythonCode && (
                <div>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {t('pages.onboardingRequestStatus.pythonCallMCPServers')}
                  </h4>
                  <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-3)' }}>
                    {t('pages.onboardingRequestStatus.editCodeHint')} {request?.services?.map(s => s.name).join(', ')}
                  </p>
                  <textarea
                    value={pythonCode}
                    onChange={(e) => setPythonCode(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: 280,
                      padding: 12,
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      color: 'var(--text-2)',
                      lineHeight: 1.4,
                      marginBottom: 12,
                      resize: 'vertical'
                    }}
                  />
                  <button
                    className={`ent-btn ${copiedTab === 'py-copy' ? 'success' : ''}`}
                    onClick={() => copyToClipboard(pythonCode, 'py-copy')}
                    style={{ fontSize: 12 }}
                  >
                    <Icon name={copiedTab === 'py-copy' ? 'check' : 'copy'} size={14} />
                    {copiedTab === 'py-copy' ? t('pages.onboardingRequestStatus.copied') : t('pages.onboardingRequestStatus.copyCode')}
                  </button>
                </div>
              )}

              {/* cURL Tab */}
              {activeTab === 'curl' && curlCode && (
                <div>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {t('pages.onboardingRequestStatus.curlCallMCPServers')}
                  </h4>
                  <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-3)' }}>
                    {t('pages.onboardingRequestStatus.editCodeHint')} {request?.services?.map(s => s.name).join(', ')}
                  </p>
                  <textarea
                    value={curlCode}
                    onChange={(e) => setCurlCode(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: 280,
                      padding: 12,
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      color: 'var(--text-2)',
                      lineHeight: 1.4,
                      marginBottom: 12,
                      resize: 'vertical'
                    }}
                  />
                  <button
                    className={`ent-btn ${copiedTab === 'curl-copy' ? 'success' : ''}`}
                    onClick={() => copyToClipboard(curlCode, 'curl-copy')}
                    style={{ fontSize: 12 }}
                  >
                    <Icon name={copiedTab === 'curl-copy' ? 'check' : 'copy'} size={14} />
                    {copiedTab === 'curl-copy' ? t('pages.onboardingRequestStatus.copied') : t('pages.onboardingRequestStatus.copyCommand')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.open && (
        <Modal
          title={notificationModal.title}
          icon="warning"
          onClose={() => setNotificationModal({ open: false, title: '', message: '' })}
          footer={
            <button
              className="ent-btn primary"
              onClick={() => setNotificationModal({ open: false, title: '', message: '' })}
            >
              <Icon name="check" /> {t('pages.onboardingRequestStatus.ok')}
            </button>
          }
        >
          <p style={{ fontSize: 14, color: 'var(--text)' }}>{notificationModal.message}</p>
        </Modal>
      )}
    </div>
  );
};

export default OnboardingRequestStatus;
